import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { isOriginInUse } from '../../../util/search';
import { parse } from '../../../util/urlHelpers';
import { createResourceLocatorString } from '../../../util/routes';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';

import { IconLocation, OutsideClickHandler, LocationAutocompleteInput } from '../../../components';

import css from './LocationFilter.module.css';

const LocationFilterComponent = props => {
  const [isOpen, setIsOpen] = useState(false);
  const { className, rootClassName, config, alignLeft, urlQueryParams, onSubmit, history, location, currentUser, ...rest } = props;
  const intl = useIntl();
  const appConfig = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  
  // Get config from props or rest
  const filterConfig = config || rest.config || rest;

  const classes = classNames(rootClassName || css.root, className);

  // Get initial location from URL params
  const { address, origin, bounds } = urlQueryParams || {};
  
  // Check if user has service address
  const getUserServiceAddress = () => {
    if (!currentUser) {
      return null;
    }
    
    const protectedData = currentUser?.attributes?.profile?.protectedData || {};
    const publicData = currentUser?.attributes?.profile?.publicData || {};
    const userType = publicData.userType;
    
    // Check for service address in multiple possible locations
    let serviceAddress = null;
    
    if (protectedData.serviceAddress) {
      serviceAddress = protectedData.serviceAddress;
    } else if (protectedData.address) {
      serviceAddress = protectedData.address;
    } else if (publicData.address && typeof publicData.address === 'string') {
      serviceAddress = publicData.address;
    } else if (publicData.address && publicData.address.address) {
      serviceAddress = publicData.address.address;
    } else if (publicData.address && publicData.address.search) {
      serviceAddress = publicData.address.search;
    } else if (publicData.address && publicData.address.selectedPlace && publicData.address.selectedPlace.address) {
      serviceAddress = publicData.address.selectedPlace.address;
    }
    
    // Check if user has the correct user type and service address
    if (userType === 'customer-main' && serviceAddress) {
      // Return the full address object with geographic data if available
      if (typeof serviceAddress === 'object' && serviceAddress.selectedPlace) {
        return serviceAddress;
      } else if (typeof serviceAddress === 'object' && serviceAddress.search) {
        return serviceAddress;
      } else if (typeof serviceAddress === 'string') {
        return serviceAddress;
      }
      return null;
    }
    
    return null;
  };
  
  // Get initial location from URL params or user's service address
  const initialLocation = address && bounds ? address : null;
  const userServiceAddress = getUserServiceAddress();
  
  // Initialize selectedLocation with initial location from URL or user's service address
  const [selectedLocation, setSelectedLocation] = useState(initialLocation || userServiceAddress);
  
  // Initialize inputValue with address from URL if available, otherwise use user's service address
  const getInitialInputValue = () => {
    if (address && bounds) {
      return {
        search: address,
        predictions: [],
        selectedPlace: { address, origin, bounds }
      };
    } else if (userServiceAddress) {
      return {
        search: userServiceAddress,
        predictions: [],
        selectedPlace: { 
          address: userServiceAddress, 
          origin: null, 
          bounds: null 
        }
      };
    }
    return { search: '', predictions: [], selectedPlace: null };
  };
  
  const [inputValue, setInputValue] = useState(getInitialInputValue());
  
  // Update state when URL changes
  useEffect(() => {
    const { address: newAddress, origin: newOrigin, bounds: newBounds } = urlQueryParams || {};
    
    // Check if the URL contains a broken address ([object Object])
    const isBrokenAddress = newAddress === '[object Object]' || (typeof newAddress === 'object' && newAddress !== null);
    
    if (newAddress && newBounds && !isBrokenAddress) {
      setSelectedLocation(newAddress);
      setInputValue({
        search: newAddress,
        predictions: [],
        selectedPlace: { address: newAddress, origin: newOrigin, bounds: newBounds }
      });
    } else if (userServiceAddress && !newAddress) {
      // If no URL params but user has service address, just set the display without updating URL
      let addressString = '';
      
      if (typeof userServiceAddress === 'object' && userServiceAddress.search) {
        addressString = userServiceAddress.search;
      } else if (typeof userServiceAddress === 'string') {
        addressString = userServiceAddress;
      }
      
      if (addressString) {
        setSelectedLocation(addressString);
        setInputValue({
          search: addressString,
          predictions: [],
          selectedPlace: { 
            address: addressString, 
            origin: null, 
            bounds: null 
          }
        });
      }
    } else {
      setSelectedLocation(null);
      setInputValue({ search: '', predictions: [], selectedPlace: null });
    }
  }, [urlQueryParams, userServiceAddress]);
  
  const formatLocation = location => {
    if (!location) return null;
    
    // Always return a string
    if (typeof location === 'string') {
      return location;
    }
    
    if (location && typeof location === 'object') {
      // Handle the new structure: {predictions, search, selectedPlace}
      if (location.search) {
        return location.search;
      }
      
      // Handle the old structure: {address, origin, bounds}
      if (location.address) {
        return location.address;
      }
      
      // Handle selectedPlace structure
      if (location.selectedPlace && location.selectedPlace.address) {
        return location.selectedPlace.address;
      }
    }
    
    return intl.formatMessage({ id: 'LocationFilter.placeholder' });
  };

  const handleLocationChange = value => {
    
    // Update the input value state
    setInputValue(value);
    
    if (value?.selectedPlace) {
      const { search, selectedPlace } = value;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(appConfig) ? { origin } : {};

      const locationData = {
        ...originMaybe,
        address: search,
        bounds,
      };

      // Store only the string address for display, not the complex object
      setSelectedLocation(search);
      setIsOpen(false);
      
      // Update URL directly like Topbar does
      if (history && location) {
        const currentSearchParams = parse(location.search, {
          latlng: ['origin'],
          latlngBounds: ['bounds'],
        });
        
        const searchParams = {
          ...currentSearchParams,
          ...locationData,
        };

        const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
          routeConfiguration,
          location
        );

        const newURL = createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams);

        history.push(newURL);
      } else if (onSubmit) {
        // Fallback to standard filter submission if history/location not available
        onSubmit(locationData);
      }
    }
  };

  const currentLocation = selectedLocation || initialLocation;
  const displayText = formatLocation(currentLocation);
  
  // Safety check: ensure displayText is always a string
  const safeDisplayText = typeof displayText === 'string' ? displayText : intl.formatMessage({ id: 'LocationFilter.placeholder' });

  // Compute the CSS class for the label with an "active" modifier if there is a selection or if the picker is open.
  const labelClasses = classNames(css.label, {
    [css.active]: currentLocation || isOpen,
  });

  // Compute the CSS class for the icon with an "active" modifier if there is a selection or if the picker is open.
  const iconClasses = classNames(css.locationIcon, {
    [css.active]: currentLocation || isOpen,
  });

  // Compute the CSS class for the toggle button with an "active" modifier if there is a selection or if the picker is open.
  const toggleButtonClasses = classNames(css.toggleButton, {
    [css.active]: currentLocation || isOpen,
  });

  return (
    <OutsideClickHandler className={classes} onOutsideClick={() => setIsOpen(false)}>
      <div
        role="button"
        className={toggleButtonClasses}
        onClick={() => setIsOpen(prevState => !prevState)}
        tabIndex={0}
        onKeyDown={() => setIsOpen(prevState => !prevState)}
      >
        <IconLocation className={iconClasses} />
        <span className={labelClasses}>
          {safeDisplayText}
        </span>
      </div>
      {isOpen ? (
        <div className={classNames(css.locationPicker, {
          [css.alignLeft]: alignLeft,
        })}>
          <LocationAutocompleteInput
            className={css.locationInput}
            iconClassName={css.locationIcon}
            inputClassName={css.locationInputField}
            predictionsClassName={css.locationPredictions}
            placeholder={intl.formatMessage({ id: 'LocationFilter.searchPlaceholder' })}
            closeOnBlur={true}
            useDarkText={true}
            config={appConfig}
            input={{
              value: inputValue,
              onChange: handleLocationChange,
              onFocus: () => {}, // Add empty onFocus function
              onBlur: () => {}, // Add empty onBlur function
            }}
          />
        </div>
      ) : null}
    </OutsideClickHandler>
  );
};

export default LocationFilterComponent; 